import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { computeModuleNavOverflow } from '../utils/moduleNavOverflow';

/**
 * Module button bar for the main app header.
 *
 * items: [{ key, label, href, onClick, active, group: 'nav' | 'admin', spacerBefore, style, badges, alert, title }]
 *
 * A hidden measurement layer renders every item in its natural layout; a
 * ResizeObserver on the bar re-runs computeModuleNavOverflow() so trailing
 * buttons move into a "More ▾" menu instead of squishing / overlapping. The
 * active module is always swapped back onto the bar.
 *
 * Items with an `href` render as real links so right-click "Open link in new
 * tab", middle-click and Ctrl/Cmd/Shift-click work natively. Only an
 * unmodified primary click is intercepted (preventDefault + onClick), so the
 * in-app switch stays instant with no page reload.
 */

/** True for a plain left click the app should handle in-page. */
export function isPlainLeftClick(event) {
  return !event.defaultPrevented
    && event.button === 0
    && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function linkHandlers(item, beforeNavigate) {
  return {
    onClick: (event) => {
      if (!isPlainLeftClick(event)) return;
      event.preventDefault();
      if (beforeNavigate) beforeNavigate();
      if (item.onClick) item.onClick();
    },
    // Links only activate on Enter; Space kept working when these were buttons.
    onKeyDown: (event) => {
      if (event.key !== ' ') return;
      event.preventDefault();
      if (beforeNavigate) beforeNavigate();
      if (item.onClick) item.onClick();
    },
  };
}

function ItemButton({ item, tabIndex, measureIdx }) {
  const className = item.group === 'admin'
    ? `module-header-nav-btn module-header-admin-btn ${item.active ? 'active' : ''}`
    : `module-header-nav-btn ${item.active ? 'active' : ''}`;
  if (item.href) {
    return (
      <a
        href={item.href}
        className={className}
        style={item.style}
        {...linkHandlers(item)}
        title={item.title}
        tabIndex={tabIndex}
        aria-current={item.active ? 'page' : undefined}
        data-nav-idx={measureIdx}
      >
        {item.label}
        {item.badges}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={className}
      style={item.style}
      onClick={item.onClick}
      title={item.title}
      tabIndex={tabIndex}
      aria-current={item.active ? 'page' : undefined}
      data-nav-idx={measureIdx}
    >
      {item.label}
      {item.badges}
    </button>
  );
}

function renderRow(list, { measure = false, indexOf } = {}) {
  const navItems = list.filter((it) => it.group !== 'admin');
  const adminItems = list.filter((it) => it.group === 'admin');
  const tabIndex = measure ? -1 : undefined;
  return (
    <>
      {navItems.map((item, i) => (
        <React.Fragment key={item.key}>
          {item.spacerBefore && i > 0 && (
            <div className="module-header-zone-divider module-header-nav-hub-spacer" aria-hidden="true" />
          )}
          <ItemButton item={item} tabIndex={tabIndex} measureIdx={measure ? indexOf(item) : undefined} />
        </React.Fragment>
      ))}
      {adminItems.length > 0 && (
        <>
          {navItems.length > 0 && <div className="module-header-zone-divider module-nav-bar__group-divider" aria-hidden="true" />}
          <div className="module-header-admin-row" role={measure ? undefined : 'group'} aria-label={measure ? undefined : 'Admin modules'}>
            {adminItems.map((item) => (
              <ItemButton key={item.key} item={item} tabIndex={tabIndex} measureIdx={measure ? indexOf(item) : undefined} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

export function ModuleNavMoreMenu({ items, measure = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const hasActive = items.some((it) => it.active);
  const hasAlert = items.some((it) => it.alert);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    const first = menuRef.current?.querySelector('[role="menuitem"]');
    if (first) first.focus();
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onMenuKeyDown = (e) => {
    const nodes = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
    const idx = nodes.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      nodes[(idx + 1) % nodes.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nodes[(idx - 1 + nodes.length) % nodes.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      nodes[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      nodes[nodes.length - 1]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="module-nav-bar__more" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`module-header-nav-btn module-nav-bar__more-btn${hasActive ? ' active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        tabIndex={measure ? -1 : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        More <span aria-hidden="true">&nbsp;▾</span>
        {hasAlert && <span className="module-header-nav-badge-dot" aria-hidden="true" />}
      </button>
      {open && !measure && (
        <div className="module-nav-bar__menu" role="menu" aria-label="More modules" ref={menuRef} onKeyDown={onMenuKeyDown}>
          {items.map((item) => (item.href ? (
            <a
              key={item.key}
              href={item.href}
              role="menuitem"
              className={`module-nav-bar__menu-item${item.active ? ' active' : ''}`}
              title={item.title}
              aria-current={item.active ? 'page' : undefined}
              {...linkHandlers(item, () => setOpen(false))}
            >
              <span className="module-nav-bar__menu-label">{item.label}</span>
              {item.badges}
            </a>
          ) : (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={`module-nav-bar__menu-item${item.active ? ' active' : ''}`}
              title={item.title}
              onClick={() => {
                setOpen(false);
                if (item.onClick) item.onClick();
              }}
            >
              <span className="module-nav-bar__menu-label">{item.label}</span>
              {item.badges}
            </button>
          )))}
        </div>
      )}
    </div>
  );
}

function sameLayout(a, b) {
  return a.visible.join(',') === b.visible.join(',') && a.overflow.join(',') === b.overflow.join(',');
}

export default function ModuleNavBar({ items = [], className = '' }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const moreMeasureRef = useRef(null);
  const [layout, setLayout] = useState(() => ({ visible: items.map((_, i) => i), overflow: [] }));

  // Latest measure fn in a ref so the ResizeObserver never needs re-binding.
  const measureFnRef = useRef(() => {});
  measureFnRef.current = () => {
    const container = containerRef.current;
    const layer = measureRef.current;
    if (!container || !layer) return;
    const origin = layer.getBoundingClientRect().left;
    const ends = [];
    const widths = [];
    items.forEach((_, i) => {
      const el = layer.querySelector(`[data-nav-idx="${i}"]`);
      const rect = el ? el.getBoundingClientRect() : { right: origin, width: 0 };
      ends.push(rect.right - origin);
      widths.push(rect.width);
    });
    const gapRaw = parseFloat(window.getComputedStyle(layer).columnGap);
    const gap = Number.isFinite(gapRaw) ? gapRaw : 8;
    const moreWidth = (moreMeasureRef.current?.getBoundingClientRect().width || 0) + gap;
    const next = computeModuleNavOverflow({
      ends,
      widths,
      available: container.clientWidth,
      moreWidth,
      gap,
      activeIndex: items.findIndex((it) => it.active),
    });
    setLayout((prev) => (sameLayout(prev, next) ? prev : next));
  };

  // Re-measure after every render (labels/badges change widths) — cheap, and
  // setLayout bails out when nothing moved.
  useLayoutEffect(() => {
    measureFnRef.current();
  });

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !containerRef.current) return undefined;
    const ro = new ResizeObserver(() => measureFnRef.current());
    ro.observe(containerRef.current);
    if (measureRef.current) ro.observe(measureRef.current);
    return () => ro.disconnect();
  }, []);

  const indexOf = (item) => items.indexOf(item);
  const visibleIdx = layout.visible.filter((i) => i < items.length);
  const overflowIdx = layout.overflow.filter((i) => i < items.length);
  // Items not measured yet stay visible until the next measure pass.
  const known = new Set([...visibleIdx, ...overflowIdx]);
  items.forEach((_, i) => { if (!known.has(i)) visibleIdx.push(i); });
  const visibleItems = visibleIdx.sort((a, b) => a - b).map((i) => items[i]);
  const overflowItems = overflowIdx.map((i) => items[i]);

  return (
    <nav className={`module-nav-bar ${className}`.trim()} aria-label="Modules" ref={containerRef}>
      <div className="module-nav-bar__measure-clip" aria-hidden="true">
        <div className="module-nav-bar__row module-nav-bar__measure" ref={measureRef}>
          {renderRow(items, { measure: true, indexOf })}
          <div ref={moreMeasureRef} className="module-nav-bar__more-measure">
            <ModuleNavMoreMenu items={[]} measure />
          </div>
        </div>
      </div>
      <div className="module-nav-bar__row module-nav-bar__row--visible" data-testid="module-nav-visible">
        {renderRow(visibleItems)}
        {overflowItems.length > 0 && <ModuleNavMoreMenu items={overflowItems} />}
      </div>
    </nav>
  );
}
